// Normal deps
import delegate from "delegate";
import domready from "domready";
import viewloader from "viewloader";
import LazyLoad from "vanilla-lazyload";
import bus from "./bus";
import views from "./views";
import createHistory from "./history-manager";
import { cookiesSupported } from "./utilities";
import { FLASH } from "./action-types";
import { navigateEvents } from "./events";
import { historyProgressEvents } from "./history-progress";
import { setupAppsignal } from "./appsignal";

try {
  setupAppsignal("main");
} catch (e) {
  console.error(e);
}

const scrollPositions = {};

domready(() => {
  let progressElement = document.querySelector(".history-progress");
  let contentElement = document.querySelector(".layout__content");
  const playerElement = document.querySelector(".player-panel");
  const abstractPlayerElement = document.querySelector(
    "[data-view-abstract-player]"
  );
  let lazyLoader = null;

  // Set up separate managers
  const progressViewManager = viewloader(views, progressElement, true);
  let contentViewManager = viewloader(views, contentElement, true);
  const playerViewManager = viewloader(views, playerElement, true);
  const abstractPlayerViewManager = viewloader(
    views,
    abstractPlayerElement,
    true
  );

  // Execute normal viewloader stuff for all instances
  progressViewManager.callViews();
  contentViewManager.callViews();
  playerViewManager.callViews();
  abstractPlayerViewManager.callViews();

  // Check for cookie support
  if (!cookiesSupported) {
    bus.emit(FLASH, {
      type: "alert",
      message:
        "Your browser has cookies disabled. Please enable them to subscribe, sign in, or listen to radio on-demand.",
      autoDismiss: false
    });
  }

  function createLazyLoader() {
    if (lazyLoader) {
      lazyLoader.destroy();
    }
    const container = getScrollReference();
    lazyLoader = new LazyLoad({
      container,
      elements_selector: "[data-lazy]"
    });
  }

  function getScrollReference() {
    if (
      document.documentElement.classList.contains("breakpoint-widescreen") ||
      document.documentElement.classList.contains("breakpoint-desktop")
    ) {
      return contentElement;
    } else {
      return document.documentElement;
    }
  }

  function scrollReference(value, relative = false) {
    const reference = getScrollReference();
    if (reference === document.documentElement) {
      if (value != null) {
        value = relative === true ? window.scrollY + value : value;
        window.scrollTo(0, value);
      }
      return window.scrollY;
    } else {
      if (value != null) {
        value = relative === true ? reference.scrollTop + value : value;
        reference.scrollTop = value;
      }
      return reference.scrollTop;
    }
  }

  function scrollToHash(hash) {
    if (hash === "") {
      return;
    }
    hash = hash.substr(1);
    const reference =
      document.getElementById(hash) ||
      document.querySelector(`a[name="${hash}"]`);
    if (reference) {
      const { top } = reference.getBoundingClientRect();
      scrollReference(top - 20, true);
    }
  }
  // Check for existing hash
  if (window.location.hash) {
    scrollToHash(window.location.hash);
  }

  window.addEventListener("hashchange", e => {
    scrollToHash(window.location.hash);
  });

  // Set up history
  const history = createHistory({ scopeSelector: ".content" });
  history.on("request-start", () => {
    bus.emit(historyProgressEvents.ACTIVATE);
  });
  history.on("before-render", (scope, action, location) => {
    bus.emit(navigateEvents.BEFORE_RENDER);
    scrollPositions[location] = scrollReference();
    contentViewManager.destroyViews();
  });
  history.on("after-render", (scope, action, location) => {
    bus.emit(navigateEvents.AFTER_RENDER);
    bus.emit(historyProgressEvents.DEACTIVATE);
    // Rebind viewloader
    contentElement = document.querySelector(".layout__content");
    contentViewManager = viewloader(views, contentElement, true);
    contentViewManager.callViews();
    createLazyLoader();

    // Scroll back to matching position
    if (action === "POP") {
      scrollReference(scrollPositions[location]);
    } else {
      if (window.location.hash) {
        scrollToHash(window.location.hash);
      } else {
        scrollReference(0);
      }
    }

    // Send a pageview for the current location
    if (window.ga) {
      window.ga("send", "pageview", location);
    }
  });

  // Create initial lazy loader
  createLazyLoader();

  // Handle scrolling anchors
  delegate(document, "a[href^='#']", "click", e => {
    e.preventDefault();
    let hash = e.delegateTarget.getAttribute("href");
    if (hash) {
      scrollToHash(hash);
    }
  });
});
